import { useState } from "react";

import { NvAlertDialog, NvButton, NvCheckbox, NvFormField } from "@/components/navigator";
import { NvLockSmallIcon } from "@/components/navigator/nv-icons-mypage";
import { useAuth } from "@/contexts/auth-context";
import { getApiError, getApiFieldErrors } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { updateMyProfile, withdrawMe } from "@/services/navigator/my-page-service";

import { MyPageModal } from "./my-page-modal";
import { MyPageHeader, useMyPage } from "./my-page-shared";
import { TempUserTypeSwitch } from "./temp-user-type-switch"; // TEMP(user-type-switch)

// 내 정보 (Figma ver.7 847:47574 · 정보 수정 847:51171): 기본 정보 카드(820, 2열 340) + [정보 수정] → 이름 · 전화번호만 고쳐요, 계정 카드(360) + [로그아웃] · 회원 탈퇴.
// 정보 수정(제안 20): 수정할 수 있는 칸은 흰 입력 칸, 수정 불가 항목도 같은 높이(40)의 회색 칸 + 잠금 아이콘, 변경 경로를 안내 문구로.
// 회원 탈퇴 확인 (Figma 847:47659): 안내 4줄 · 본인 확인(가입 이메일) · 동의 체크 → [탈퇴하기], 닫기는 아이콘만(Lucide x).

const show = (value: string | null | undefined) => (value && value !== "n/a" ? value : "-");

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[12px] leading-[14px] text-nv-text-secondary">{label}</span>
      <span className="text-[15px] font-medium leading-[18px] text-nv-text-primary">{show(value)}</span>
    </div>
  );
}

// 정보 수정 · 수정 불가 칸: 회색 #F4F5F7, r10, 높이 40, 오른쪽 잠금
function ReadonlyField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[12px] leading-[14px] text-nv-text-secondary">{label}</span>
      <div className="flex h-10 w-full max-w-[300px] items-center justify-between gap-2 rounded-nv-control bg-[#F4F5F7] px-3">
        <span className="min-w-0 truncate text-[15px] font-medium leading-[18px] text-[#5E616B]">{show(value)}</span>
        <NvLockSmallIcon className="shrink-0" />
      </div>
    </div>
  );
}

// 정보 수정 · 수정 가능 칸: 흰 칸, 1px #CBD1D6, 포커스 주황
function EditField({
  label,
  error,
  ...input
}: { label: string; error?: string } & Omit<React.InputHTMLAttributes<HTMLInputElement>, "className">) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[12px] leading-[14px] text-nv-text-secondary">{label}</span>
      <input
        aria-invalid={error ? true : undefined}
        className={cn(
          "h-10 w-full max-w-[300px] rounded-nv-control border bg-nv-surface px-3 text-[15px] leading-[18px] text-nv-text-primary placeholder:text-nv-text-placeholder focus-visible:border-nv-brand focus-visible:outline-none",
          error ? "border-nv-danger" : "border-nv-border-strong",
        )}
        {...input}
      />
      {error && <span className="text-[12px] leading-[14px] text-nv-danger">{error}</span>}
    </label>
  );
}

function formatPhone(raw: string) {
  const digits = raw.replace(/\D/g, "").slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, digits.length - 4)}-${digits.slice(-4)}`;
}

export function MyProfileSection() {
  const { summary, refresh, onWithdraw } = useMyPage();
  const { logout, refreshUser } = useAuth();
  const p = summary.profile;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(p.name ?? "");
  const [phone, setPhone] = useState(formatPhone(p.phone ?? ""));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);

  const startEdit = () => {
    setName(p.name ?? "");
    setPhone(formatPhone(p.phone ?? ""));
    setErrors({});
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await updateMyProfile({ name, phone });
      setEditing(false);
      refresh();
      refreshUser().catch(() => undefined);
    } catch (err) {
      const fields = getApiFieldErrors(err);
      setErrors(fields);
      // No field to hang the message on (server / network failure): tell the user in a popup instead of staying silent
      if (Object.keys(fields).length === 0) setAlert(getApiError(err)?.message ?? "저장하지 못했어요. 잠시 후 다시 시도해 주세요.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <MyPageHeader title="내 정보" description="프로필과 계정을 관리합니다" />
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-col gap-5 rounded-nv-card border border-nv-border bg-nv-surface p-7 lg:w-[820px]">
          <div className="flex items-center justify-between">
            <h2 className="text-[17px] font-medium leading-5 text-nv-text-primary">기본 정보</h2>
            {editing ? (
              <div className="flex gap-2">
                <NvButton variant="secondary" size="sm" onClick={() => setEditing(false)}>
                  취소
                </NvButton>
                {/* Figma ver.7: 취소 · 저장 모두 높이 40 · 모서리 10 · 500 */}
                <NvButton size="sm" onClick={save} disabled={saving} className="h-10 rounded-nv-control font-medium">
                  저장
                </NvButton>
              </div>
            ) : (
              <NvButton variant="secondary" size="sm" onClick={startEdit}>
                정보 수정
              </NvButton>
            )}
          </div>
          {editing ? (
            <div className="grid gap-x-0 gap-y-[22px] md:grid-cols-[340px_340px]">
              <EditField label="이름" value={name} maxLength={50} autoFocus onChange={(e) => setName(e.target.value)} error={errors.name} />
              <ReadonlyField label="이메일" value={p.email} />
              <ReadonlyField label="법인명" value={p.company_name} />
              <ReadonlyField label="부서" value={p.department} />
              <ReadonlyField label="직급 / 직책" value={p.position} />
              <EditField
                label="전화번호"
                inputMode="numeric"
                value={phone}
                placeholder="010-0000-0000"
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                error={errors.phone}
              />
              <ReadonlyField label="기업 소재국" value={p.company_country} />
              <ReadonlyField label="가입일" value={p.joined_at} />
            </div>
          ) : (
            <div className="grid gap-x-0 gap-y-[22px] md:grid-cols-[340px_340px]">
              <Field label="이름" value={p.name} />
              <Field label="이메일" value={p.email} />
              <Field label="법인명" value={p.company_name} />
              <Field label="부서" value={p.department} />
              <Field label="직급 / 직책" value={p.position} />
              <Field label="전화번호" value={p.phone ? formatPhone(p.phone) : null} />
              <Field label="기업 소재국" value={p.company_country} />
              <Field label="가입일" value={p.joined_at} />
            </div>
          )}
          <p className="text-[12px] leading-[14px] text-nv-text-secondary">
            {editing
              ? "흰 칸(이름 · 전화번호)만 수정할 수 있어요 · 회색 칸은 회사 · 로그인 계정 정보라 변경이 필요하면 Contact Us 의 시스템 사용 문의로 요청해 주세요"
              : "이메일은 로그인 계정과 연결되어 수정할 수 없어요."}
          </p>
        </div>

        <div className="flex flex-col gap-4 rounded-nv-card border border-nv-border bg-nv-surface p-7 lg:w-[360px]">
          <h2 className="text-[17px] font-medium leading-5 text-nv-text-primary">계정</h2>
          {[
            { label: "로그인 방식", value: p.login_method, tone: "" },
            { label: "회원 유형", value: p.user_type_label, tone: "" },
            { label: "계정 상태", value: p.status_label, tone: "success" },
          ].map((f) => (
            <div key={f.label} className="flex items-center justify-between gap-3 text-[13px] leading-4">
              <span className="text-nv-text-secondary">{f.label}</span>
              <span className={f.tone === "success" ? "font-bold text-nv-success" : "font-medium text-nv-text-primary"}>{f.value}</span>
            </div>
          ))}
          <hr className="border-nv-divider" />
          <div className="flex items-center justify-between">
            <NvButton variant="secondary" size="sm" onClick={() => logout()}>
              로그아웃
            </NvButton>
            {p.can_withdraw && (
              <button type="button" onClick={onWithdraw} className="text-[13px] leading-4 text-nv-text-secondary hover:text-nv-text-primary">
                회원 탈퇴
              </button>
            )}
          </div>
        </div>
      </div>
      <TempUserTypeSwitch /> {/* TEMP(user-type-switch) */}
      <NvAlertDialog open={alert !== null} onOpenChange={(o) => !o && setAlert(null)} message={alert ?? ""} actions={<NvButton onClick={() => setAlert(null)}>확인</NvButton>} />
    </>
  );
}

const WITHDRAW_NOTICES = [
  "탈퇴 후에는 로그인할 수 없습니다.",
  "개인정보는 관련 법령에 따라 파기 또는 분리 보관됩니다.",
  "문의·상담 이력 보관 기준은 정책 확정 후 반영됩니다.",
  "같은 이메일로 다시 가입할 수 있습니다.",
];

export function WithdrawDialog({ open, onOpenChange, onDone }: { open: boolean; onOpenChange: (open: boolean) => void; onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const close = (next: boolean) => {
    if (!next) {
      setEmail("");
      setAgreed(false);
      setError(null);
    }
    onOpenChange(next);
  };
  const submit = async () => {
    setBusy(true);
    try {
      await withdrawMe({ email, agreed });
      onOpenChange(false);
      onDone();
    } catch (err) {
      const fields = getApiFieldErrors(err);
      setError(fields.email ?? fields.agreed ?? getApiError(err)?.message ?? "탈퇴를 처리하지 못했어요.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <MyPageModal
      open={open}
      onOpenChange={close}
      title="회원 탈퇴"
      width={480}
      footer={
        <div className="flex w-full justify-end gap-2">
          <NvButton variant="secondary" onClick={() => close(false)}>
            취소
          </NvButton>
          <button
            type="button"
            onClick={submit}
            disabled={!agreed || !email.trim() || busy}
            className="h-[39px] self-center rounded-nv-chip bg-nv-text-primary px-5 text-[14px] font-bold leading-[17px] text-nv-text-inverse disabled:opacity-40"
          >
            탈퇴하기
          </button>
        </div>
      }
    >
      <p className="text-[14px] font-medium leading-[17px] text-nv-text-primary">탈퇴 전 아래 내용을 확인해 주세요</p>
      <ul className="flex flex-col gap-[6px] rounded-nv-card bg-nv-bg-page p-4">
        {WITHDRAW_NOTICES.map((line) => (
          <li key={line} className="text-[13px] leading-4 text-nv-text-secondary">
            · {line}
          </li>
        ))}
      </ul>
      <NvFormField
        label="본인 확인"
        required
        type="email"
        value={email}
        placeholder="가입한 이메일을 입력해 주세요"
        onChange={(e) => setEmail(e.target.value)}
        error={error ?? undefined}
      />
      <NvCheckbox checked={agreed} onCheckedChange={setAgreed} label="안내 내용을 확인했으며 탈퇴에 동의합니다" labelClassName="text-[13px] leading-4" />
    </MyPageModal>
  );
}
